// src/stores/ctx.store.ts

import type { UserResource } from '@clerk/shared/types'
import { consola as consolaLogger } from 'consola'
import moment from 'moment'
import { defineStore } from 'pinia'
import type { Observer } from 'rxjs'
import { Observable, catchError, first, of, retry, shareReplay, tap, timer, zip } from 'rxjs' // Import catchError and of
import { useAppMachine } from '~/composables/useAppMachine'
import type { GenericPayloadResponse } from '~/protos/gen/stcs_commons.entity'
import type { StcsComp } from '~/protos/gen/stcs_comp.entity'
import type { StcsCompAccMapping } from '~/protos/gen/stcs_comp_acc_mapping.entity'
import type { StcsCompActualsResValues } from '~/protos/gen/stcs_comp_actuals_res_values.entity'
import type { StcsCompFssFlat } from '~/protos/gen/stcs_comp_fss_flat.entity'
import type { StcsCompKpiCfg } from '~/protos/gen/stcs_comp_kpi_cfg.entity'
import type { StcsCompKpiResValues } from '~/protos/gen/stcs_comp_kpi_res_values.entity'
import type { StcsCompPlanResValues } from '~/protos/gen/stcs_comp_plan_res_values.entity'
import { type StcsCompRoleEnum, StcsEntityStatusEnum, type StcsOrgRoleEnum, StcsResCode } from '~/protos/gen/stcs_enums.entity'
import type { StcsOrg } from '~/protos/gen/stcs_org.entity.ts'
import type { StcsOrgFsdFlat } from '~/protos/gen/stcs_org_fsd_flat.entity'
import type { CompKpiResValuesGetRequest } from '~/protos/gen/stcs_wapp_svc_comp_kpi'
import type { CompPlanCfgGetRequest, CompPlanCfgListRequest, CompPlanRfGetRequest } from '~/protos/gen/stcs_wapp_svc_comp_plan'
import type { GetWappSettingsRequest, UpdateCompUserWappSettingsRequest, UpdateCompWappSettingsRequest, UpdateOrgWappSettingsRequest } from '~/protos/gen/stcs_wapp_svc_settings'
import {
  cmwCompActualsResValuesGetRequest,
  cmwCompByUserListRequest,
  cmwCompFssFlatGetRequest,
  cmwCompKpiCfgCalcRequest,
  cmwCompKpiResValuesGetRequest,
  cmwCompPlanCfgGetRequest,
  cmwCompPlanCfgListRequest,
  cmwCompPlanRfGetRequest,
  cmwDmCompAcmGetRequest,
  cmwDmOrgFsdFlatGetRequest,
  cmwGetOrgWappSettingsRequest,
  cmwOrgListRequest,
  cmwUpdateCompUserWappSettingsRequest,
  cmwUpdateCompWappSettingsRequest,
  cmwUpdateOrgWappSettingsRequest,
  cmwUserGetRequest,
} from '~/services/cmw.service'
import { getLanguage } from '~/services/commons.service'
import { useAcmStore } from '~/stores/acm.store'
import { createPlanningViewStore } from '~/stores/planningview.store'
import { useSharedScopeStore } from '~/stores/sharedscope.store'
import {
  type CompanyAnalysisSettings,
  type CompanyDashboardSettings,
  type CompanyFinancialSettings,
  type ParsedWappSettings,
  type ParsedWappSettingsConfig,
  type ParsedWappUserSettingsConfig,
  type UserOrgSpecificSettings,
  parseWappSettings,
  stringifyWappSettingsConfig,
  stringifyWappUserSettingsConfig,
} from '~/types/app-settings'
import type { FinancialsGridDataAvailablePlanList } from '~/types/stcs_fpna_res_values.entity'
import type { CtxData, KpiRequestKey, SectionKey, ViewMap, WappCompData, WappNotificationPayload } from '~/types/stcs_wapp_ctx_data.entity'
import { default_ui_map } from '~/types/stcs_wapp_ctx_data.entity'

const consola = consolaLogger.withTag('CTX_STORE')

const pendingRFPlanRequests = new Map<string, Observable<StcsCompPlanResValues | undefined>>()
const pendingCompDataRequests = new Set<string>()
const pendingBackgroundRefreshRequests = new Set<string>()

function getInitialActiveSection(): SectionKey {
  if (!process.client) {
    return 'financials'
  }
  const currentPath = window.location.pathname
  const pathParts = currentPath.slice(1).split('/')
  const validSections = ['dashboard', 'financials', 'analysis', 'plan', 'settings', 'data-management']
  if (validSections.includes(pathParts[0])) {
    return pathParts[0] as SectionKey
  }
  if (pathParts.length >= 5 && validSections.includes(pathParts[4])) {
    return pathParts[4] as SectionKey
  }
  for (const part of pathParts) {
    if (validSections.includes(part)) {
      return part as SectionKey
    }
  }
  return 'financials'
}

export const useCtxStore = defineStore('ctx', {
  state: (): CtxData => ({
    idp_user: {} as CtxData['idp_user'],
    ref_user: {} as CtxData['ref_user'],
    ref_org_map: {} as CtxData['ref_org_map'],
    ref_comp_map: {} as CtxData['ref_comp_map'],
    ref_fsd_map: {} as CtxData['ref_fsd_map'],
    ref_app_settings_map: {} as CtxData['ref_app_settings_map'],
    isLoadingUser: false,
    active_org_id: '',
    active_comp_id: '',
    active_section: getInitialActiveSection(),
    appSettingLoading: false,
  }),
  actions: {
    ensureKpiRequestStatus(compData: WappCompData): void {
      if (!compData.kpi_requests) {
        compData.kpi_requests = {}
      }
    },
    setKpiRequestStatus(compData: WappCompData, key: KpiRequestKey, loading: boolean, settled: boolean, error?: string): void {
      this.ensureKpiRequestStatus(compData)
      const kpiRequestStatus = compData.kpi_requests
      if (!kpiRequestStatus) {
        return
      }
      kpiRequestStatus[key] = { loading, settled, error }
    },
    initializeKpiStore(compData: WappCompData, isCfg: boolean, forceUnsettled = false): void {
      this.ensureKpiRequestStatus(compData)

      const key: KpiRequestKey = isCfg ? 'cfg' : 'res'
      const currentStatus = compData.kpi_requests?.[key]
      const shouldPreserveSettledState = currentStatus?.settled === true && !forceUnsettled

      // Keep settled data/state stable by default to avoid unnecessary loading flicker.
      if (shouldPreserveSettledState) {
        this.setKpiRequestStatus(compData, key, false, true)
        return
      }

      if (isCfg) {
        compData.ref_comp_kpis_cfg = {} as StcsCompKpiCfg
        this.setKpiRequestStatus(compData, 'cfg', true, false)
      } else {
        compData.ref_comp_kpis_res = {} as StcsCompKpiResValues
        this.setKpiRequestStatus(compData, 'res', true, false)
      }
    },
    storeKpiData(compData: CtxData['ref_comp_map'][string], data: StcsCompKpiCfg | StcsCompKpiResValues, isCfg: boolean): void {
      if (isCfg) {
        compData.ref_comp_kpis_cfg = data as StcsCompKpiCfg
        this.setKpiRequestStatus(compData, 'cfg', false, true)
      } else {
        compData.ref_comp_kpis_res = data as StcsCompKpiResValues
        this.setKpiRequestStatus(compData, 'res', false, true)
      }
    },
    clearKpiCacheForOrg(orgId: string): void {
      if (!orgId) {
        return
      }

      Object.values(this.ref_comp_map).forEach((compData) => {
        const compOrgId = compData.ref_comp?.meta?.ref_id_org
        if (compOrgId !== orgId) {
          return
        }

        compData.ref_comp_kpis_res = {} as StcsCompKpiResValues
        compData.ref_comp_kpis_cfg = {} as StcsCompKpiCfg
        this.setKpiRequestStatus(compData, 'res', false, false)
        this.setKpiRequestStatus(compData, 'cfg', false, false)
      })
    },
    setIdpUser(idp_user: UserResource): Observable<void> {
      return new Observable((observer) => {
        if (this.idp_user.id !== idp_user.id && !this.isLoadingUser) {
          this.isLoadingUser = true
          cmwUserGetRequest({ euuid: idp_user.id.extractId() })
            .pipe(first(), retry({ delay: (_error) => timer(1000), count: 3 }))
            .subscribe({
              next: (cmwUserGetMessage: GenericPayloadResponse) => {
                this.isLoadingUser = false
                if (cmwUserGetMessage.res_code === StcsResCode.SUCCESS && cmwUserGetMessage.wapp_user) {
                  this.idp_user = idp_user
                  this.ref_user = cmwUserGetMessage.wapp_user

                  // Validate language before setting it to prevent crashes on bad data
                  const savedLang = getLanguage(cmwUserGetMessage?.wapp_user?.settings?.sel_lang || '')
                  const { locales, setLanguage } = useAppMachine()
                  if (locales.value.includes(savedLang)) {
                    setLanguage(savedLang)
                  } else {
                    consola.warn(`User setting language '${savedLang}' is invalid, keeping default.`)
                  }

                  observer.next()
                  observer.complete()
                } else {
                  observer.error(new Error('Failed to fetch user'))
                }
              },
              error: (error) => {
                this.isLoadingUser = false
                observer.error(error)
              },
            })
        } else {
          this.isLoadingUser = false
          observer.next()
          observer.complete()
        }
      })
    },
    setRefOrgList(): Observable<void> {
      return new Observable((observer) => {
        cmwOrgListRequest({ ref_id_user: this.ref_user.id.extractId() }).subscribe({
          next: (message: GenericPayloadResponse) => {
            this.isLoadingUser = false
            if ([StcsResCode.NOT_FOUND, StcsResCode.BUSINESS_ERROR].includes(message.res_code) && message.empty) {
              this.ref_org_map = {}
              observer.next()
              observer.complete()
            } else if (message.res_code === StcsResCode.SUCCESS) {
              this.ref_org_map = {}
              const orgEntries = message.stcs_org_list?.entries
              if (orgEntries && orgEntries.length > 0) {
                orgEntries.forEach((org: StcsOrg) => {
                  this.ref_org_map[org.id.extractId()] = org
                })
                this.setRefCompMap().subscribe({
                  next: () => {
                    observer.next()
                    observer.complete()
                  },
                  error: (err) => observer.error(err),
                })
              } else {
                consola.info('🟡 setRefOrgList: No organizations found for user.')
                observer.next()
                observer.complete()
              }
            } else {
              observer.error(new Error('Failed to fetch org list'))
            }
          },
          error: (error) => {
            this.isLoadingUser = false
            observer.error(error)
          },
        })
      })
    },
    setRefCompMap(): Observable<void> {
      return new Observable((observer) => {
        cmwCompByUserListRequest({ ref_id_user: this.ref_user.id.extractId() })
          .pipe(first(), retry({ delay: (_error) => timer(1000), count: 3 }))
          .subscribe({
            next: (message: GenericPayloadResponse) => {
              if ([StcsResCode.NOT_FOUND, StcsResCode.BUSINESS_ERROR].includes(message.res_code) && message.empty) {
                this.ref_comp_map = {}
                observer.next()
                observer.complete()
              } else if (message.res_code === StcsResCode.SUCCESS && message.stcs_comp_list) {
                message.stcs_comp_list.entries.forEach((comp: StcsComp) => {
                  const compId = comp.id.extractId()
                  let wappData = this.ref_comp_map[compId]

                  if (wappData) {
                    wappData.ref_comp = comp
                  } else {
                    wappData = {
                      comp_url: '',
                      has_data: false,
                      ref_comp: comp,
                      ui_map: default_ui_map,
                      availablePlanList: [],
                    }
                  }

                  this.ref_comp_map[compId] = wappData
                })

                observer.next()
                observer.complete()
              } else {
                observer.error(new Error('Failed to fetch comp list'))
              }
            },
            error: (error) => {
              this.isLoadingUser = false
              observer.error(error)
            },
          })
      })
    },
    /**
     * Load company data (FSS + actuals + ACM) for a given company.
     * @param param_comp_id - The company ID to load data for
     * @param options.backgroundRefresh - When true, silently re-fetches data while keeping
     *   has_data = true (SWR pattern). Respects a TTL to avoid redundant requests.
     */
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Merged normal + SWR background-refresh paths into one method to avoid code duplication
    changeCompById(param_comp_id: string, options?: { backgroundRefresh?: boolean }): void {
      const isBackground = options?.backgroundRefresh

      if (!this.ref_user.settings) {
        throw new Error('🔴 changeCompById: ref_user.settings is not defined')
      }
      const compId = param_comp_id ? param_comp_id.extractId() : '_none_changeCompById.cmwGetActualsResFstmtRequest_none'
      let compData = this.ref_comp_map[compId]
      if (!compData) {
        consola.error(`Company data not found for company ID: ${compId}, taking the first in the list.`)
        compData = Object.values(this.ref_comp_map)[0]
      }
      this.active_comp_id = compId

      const isOnboardingRoute = window.location.pathname.includes('/onboarding')
      const isDataManagementRoute = window.location.pathname.includes('/data-management') && !window.location.pathname.includes('/mapping')

      if (isOnboardingRoute || isDataManagementRoute) {
        consola.debug('🟡 ctx.store: changeCompById skipped automated load - Onboarding is active.')
        return
      }

      if (isBackground) {
        // SWR path: skip if data was fetched recently or a background refresh is already in-flight
        if (!compData.has_data) return
        if (!this.ref_user?.id) return

        if (pendingBackgroundRefreshRequests.has(compId)) {
          consola.debug('🟡 changeCompById(bg): Skipping — background refresh already in-flight for', compId)
          return
        }
        pendingBackgroundRefreshRequests.add(compId)
        consola.debug('🔄 changeCompById(bg): Background refresh started for', compId)
      } else {
        // Normal path: skip if already loaded or request pending
        if (compData.has_data || pendingCompDataRequests.has(compId)) {
          return
        }
        pendingCompDataRequests.add(compId)
        compData.has_data = false
      }

      const actualsResRequest = { ref_id_user: this.ref_user.id.extractId(), ref_id_comp: compId }
      const fssRequest = cmwCompFssFlatGetRequest(actualsResRequest)
      const valuesRequest = cmwCompActualsResValuesGetRequest(actualsResRequest)
      const acmRequest = this.loadAccountsRowsI18nMap(this.active_org_id, compId).pipe(
        catchError((err) => {
          consola.warn(`Could not load KPI Config for company ${compId}. This is expected if none are configured.`, err.message)
          return of(undefined)
        }),
      )

      zip(fssRequest, valuesRequest, acmRequest)
        .pipe(first(), retry({ delay: (_error) => timer(1000), count: isBackground ? 2 : 3 }))
        .subscribe({
          next: ([fssResponse, valuesResponse, acmResponse]) => {
            if (isBackground) pendingBackgroundRefreshRequests.delete(compId)
            else pendingCompDataRequests.delete(compId)

            if (fssResponse.res_code === StcsResCode.SUCCESS && fssResponse.stcs_comp_fss_flat && valuesResponse.res_code === StcsResCode.SUCCESS && valuesResponse.stcs_comp_actuals_res_values && acmResponse) {
              const compStore = this.ref_comp_map[compId]
              compStore.ref_comp_actuals_res_fstmt = fssResponse.stcs_comp_fss_flat
              compStore.ref_comp_actuals_res_values = valuesResponse.stcs_comp_actuals_res_values
              compStore.ref_comp_acc_mapping = acmResponse
              useAcmStore().loadAccMappingData(acmResponse)
              compStore.ui_map = this.generate_ui_map(compId)

              compStore.has_data = true
              if (isBackground) consola.debug('✅ changeCompById(bg): Background refresh completed for', compId)
            } else if (!isBackground) {
              compData.has_data = false
            }
          },
          error: (err) => {
            if (isBackground) {
              pendingBackgroundRefreshRequests.delete(compId)
              consola.warn('🟡 changeCompById(bg): Background refresh failed for', compId, err)
            } else {
              pendingCompDataRequests.delete(compId)
              compData.has_data = false
            }
          },
        })
    },

    /**
     * Force refresh company data, bypassing the has_data and pendingCompDataRequests checks.
     * Use this after deleting company data to ensure fresh data is loaded.
     * Clears all cached data for the company before triggering a fresh fetch.
     * @param compId - The ID of the company to force refresh
     */
    forceRefreshCompanyData(compId: string): void {
      // Clear the pending request for this company if exists
      pendingCompDataRequests.delete(compId)

      // Clear all cached data for this company
      if (this.ref_comp_map[compId]) {
        this.ref_comp_map[compId].has_data = false
      }

      // Clear shared scope store data for this company
      const sharedScopeStore = useSharedScopeStore()
      sharedScopeStore.clearCompanyDateData(compId)

      // Now trigger the normal data loading flow
      this.changeCompById(compId)
    },

    /**
     * Orchestrate background revalidation of all data for the active company and section.
     * Implements stale-while-revalidate: cached data is shown immediately while fresh data
     * is fetched silently in the background. Triggered on every section/route change.
     * Reuses existing load methods with { backgroundRefresh: true } to avoid code duplication.
     */
    revalidateSection(): void {
      const compId = this.active_comp_id
      const orgId = this.active_org_id
      if (!(compId && orgId)) return

      const compData = this.ref_comp_map[compId]
      if (!compData?.has_data) return

      // 1. Revalidate company actuals (FSS + Values + ACM)
      this.changeCompById(compId, { backgroundRefresh: true })
      this.loadFlatFSD(orgId).subscribe({
        error: (err) => consola.warn('🟡 revalidateSection: FSD refresh failed:', err),
      })

      // Skip plan-related revalidation on the planning section —
      // planningview.store manages its own data pipeline with LRU caching
      if (this.active_section === 'plan') return

      // 2. Revalidate available plans list
      this.fetchAvailablePlans(compId, orgId, { backgroundRefresh: true }).subscribe({
        error: (err) => consola.warn('🟡 revalidateSection: Plans refresh failed:', err),
      })

      // 3. Revalidate selected plan result values + RF data
      const sharedScopeStore = useSharedScopeStore()
      const selectedPlanId = sharedScopeStore.getSelectPlanId(compId)
      if (selectedPlanId) {
        this.loadPlanResValues(compId, selectedPlanId, orgId, { backgroundRefresh: true }).subscribe({
          error: (err) => consola.warn('🟡 revalidateSection: Plan values refresh failed:', err),
        })

        // 4. Revalidate RF plan data by clearing cache and re-fetching
        const lastActual = sharedScopeStore.lastActualsDate[compId]
        if (lastActual) {
          this.loadRFPlanResValues(compId, selectedPlanId, orgId, lastActual, { backgroundRefresh: true }).subscribe({
            error: (err) => consola.warn('🟡 revalidateSection: RF refresh failed:', err),
          })
        }
      }
    },

    loadFlatFSD(param_org_id: string): Observable<StcsOrgFsdFlat | undefined> {
      return new Observable((observer) => {
        if (!this.ref_user?.settings) {
          observer.error(new Error('🔴 loadFlatFSD: ref_user.settings is not defined'))
          return
        }

        const orgId = param_org_id ? param_org_id.extractId() : '_none_loadFlatFSD.cmwOrgGetFsdFlatRequest_none'
        const orgGetFsdFlatRequest = {
          ref_id_user: this.ref_user.id.extractId(),
          ref_id_org: orgId,
        }

        cmwDmOrgFsdFlatGetRequest(orgGetFsdFlatRequest)
          .pipe(first(), retry({ delay: (_error) => timer(1000), count: 3 }))
          .subscribe({
            next: (message: GenericPayloadResponse) => {
              if (message.res_code === StcsResCode.SUCCESS && message.stcs_org_fsd_flat) {
                consola.trace(`[STORE SET] Storing FSD for orgId: ${orgId}`, message.stcs_org_fsd_flat)
                useCtxStore().ref_fsd_map[orgId] = message.stcs_org_fsd_flat
                consola.debug('🟢 loadFlatFSD: stcs_org_fsd_flat loaded successfully')
              }
              observer.next(message.stcs_org_fsd_flat)
            },
            error: (error) => {
              observer.error(error)
            },
            complete: () => {
              observer.complete()
            },
          })
      })
    },

    loadAccountsRowsI18nMap(param_org_id: string, param_comp_id: string): Observable<StcsCompAccMapping | undefined> {
      return new Observable((observer) => {
        if (!this.ref_user?.settings) {
          observer.error(new Error('🔴 loadAccountsRowsI18nMap: ref_user.settings is not defined'))
          return
        }

        const orgId = param_org_id ? param_org_id.extractId() : '_none_loadAccountsRowsI18nMap.cmwOrgGetFsdFlatRequest_none'
        const compId = param_comp_id ? param_comp_id.extractId() : '_none_changeCompById.cmwGetActualsResFstmtRequest_none'

        // Skip API call if company ID is empty or invalid (e.g., during onboarding before company is created)
        if (!compId || compId === '' || compId.startsWith('_none')) {
          consola.debug('🟡 loadAccountsRowsI18nMap: Skipping API call - company ID is empty or invalid:', compId)
          observer.next(undefined)
          observer.complete()
          return
        }

        const compGeti18nMapRequest = {
          ref_id_user: this.ref_user.id.extractId(),
          ref_id_org: orgId,
          ref_id_comp: compId,
        }

        cmwDmCompAcmGetRequest(compGeti18nMapRequest)
          .pipe(first(), retry({ delay: (_error) => timer(1000), count: 3 }))
          .subscribe({
            next: (message: GenericPayloadResponse) => {
              if (message.res_code === StcsResCode.SUCCESS && message.stcs_comp_acc_mapping) {
                consola.trace(`[STORE SET] Storing Account Mapping for compId: ${compId}`, message.stcs_comp_acc_mapping)
                this.ref_comp_map[compId].ref_comp_acc_mapping = message.stcs_comp_acc_mapping
                observer.next(message.stcs_comp_acc_mapping)
              }
            },
            error: (error) => {
              observer.error(error)
            },
            complete: () => {
              observer.complete()
            },
          })
      })
    },

    userFeeedNotificationList(): WappNotificationPayload[] {
      const _unused_example = [
        {
          message: 'Your notification message here 1',
          type: 'task',
          link: 'https://example.org',
          date_time_str: '2024-11-30T16:00:00Z',
          read: true,
        },
        {
          message: 'Your notification message here2',
          type: 'info',
          date_time_str: '2024-11-30T16:00:00Z',
          read: false,
        },
        {
          message: 'Your notification message here3',
          type: 'warn',
          date_time_str: '2024-11-30T11:00:00Z',
          read: false,
        },
      ]
      return [
        {
          message: '👋 Welcome to statycs!',
          type: 'info',
          date_time_str: '2025-09-10T10:00:00Z',
          read: false,
        },
      ]
    },
    userFeeedNotificationCount() {
      return this.userFeeedNotificationList().filter((notification) => !notification.read).length
    },
    generate_ui_map(ref_comp_id: string, fssFlat?: StcsCompFssFlat, actualsResValues?: StcsCompActualsResValues): Record<SectionKey, ViewMap> {
      const comp = useCtxStore().ref_comp_map[ref_comp_id]

      const fpna_fye_month = comp.ref_comp.data?.fpna_fye_month || 1
      const ref_actuals_res_values = actualsResValues || useCtxStore().ref_comp_map[ref_comp_id].ref_comp_actuals_res_values
      if (!(comp && ref_actuals_res_values && (fssFlat || useCtxStore().ref_comp_map[ref_comp_id].ref_comp_actuals_res_fstmt))) {
        throw new Error(`🔴 generate_ui_map: ref_actuals_res_values or ref_actuals_res_fstmt is not defined for ref_comp_id: ${ref_comp_id}`)
      }
      const ui_map_res: Record<SectionKey, ViewMap> = comp.ui_map || default_ui_map
      const fpna_begin_date_string = ref_actuals_res_values.meta?.fpna_begin_date_string || ''
      const fpna_end_date_string = ref_actuals_res_values.meta?.fpna_end_date_string || ''
      const last_actuals_year = new Date(fpna_end_date_string).getFullYear()
      const last_actuals_month = new Date(fpna_end_date_string).getMonth() + 1
      const fyb_date_str = last_actuals_month === fpna_fye_month ? `${last_actuals_year}-${fpna_fye_month.toString().padStart(2, '0')}` : `${last_actuals_year - 1}-${fpna_fye_month.toString().padStart(2, '0')}`
      const range_begin_date_str = moment(fpna_begin_date_string).isAfter(fyb_date_str, 'dates') ? fpna_begin_date_string : fyb_date_str
      // Import the shared scope store to check for saved actuals date
      const sharedScopeStore = useSharedScopeStore()

      const scope_params = {
        plan_date_str: '',
        actuals_date_str: sharedScopeStore.lastActualsDate[ref_comp_id] || fpna_end_date_string || '',
        range_date_str: `${range_begin_date_str},${sharedScopeStore.lastActualsDate[ref_comp_id] || fpna_end_date_string}`,
      }

      const planningStore = createPlanningViewStore(ref_comp_id)

      const planningScopeParams = {
        plan_date_str: '',
        actuals_date_str: planningStore.lastActualsDate[ref_comp_id] || fpna_end_date_string, // Keep original behavior for planning
        range_date_str: `${range_begin_date_str},${planningStore.lastActualsDate[ref_comp_id] || fpna_end_date_string}`,
      }

      comp.ui_map.dashboard.scope_params = scope_params
      comp.ui_map.financials.scope_params = scope_params
      comp.ui_map.analysis.scope_params = scope_params
      comp.ui_map.plan.scope_params = planningScopeParams
      comp.ui_map.settings.scope_params = scope_params

      return ui_map_res
    },
    loadActiveIds(orgId: string | undefined, compId: string | undefined): void {
      // 1. Set Org ID from URL param or keep current state
      this.active_org_id = orgId || this?.active_org_id

      const selectedOrgId = this?.active_org_id
      const activeOrg = this?.ref_org_map[selectedOrgId] || null
      const org = selectedOrgId ? this?.ref_org_map[selectedOrgId] : undefined
      const compList = useCtxStore().ref_comp_map
      const refCompMap = activeOrg?.meta?.ref_comp_map || {}

      // Find the first available company in the organization to act as fallback
      const firstCompInOrg = Object.keys(org?.meta?.ref_comp_map || {}).find((_compId) => {
        return compList[_compId]?.ref_comp?.meta?.status === StcsEntityStatusEnum.SES_ACTIVE && refCompMap[_compId]?.ref_user_map[useCtxStore()?.ref_user.id.extractId()]
      })

      // 2. Set Comp ID from URL param, fallback to first available, or keep current state
      this.active_comp_id = compId || firstCompInOrg || this?.active_comp_id || ''

      // If the resulting active_comp_id is still invalid (e.g. user has no companies), ensure it's empty
      if (this.active_comp_id && !compList[this.active_comp_id]) {
        this.active_comp_id = ''
      }

      // Lazily load app settings only for the active org (not all orgs upfront)
      if (this.active_org_id && !this.ref_app_settings_map[this.active_org_id.extractId()]) {
        this.setAppSettings(this.active_org_id).subscribe({})
      }
    },
    setActiveSection(section: SectionKey): void {
      this.active_section = section
    },

    /**
     * Load app settings for specific organization (lazy loading)
     */
    setAppSettings(orgId: string): Observable<ParsedWappSettings | undefined> {
      return new Observable((observer) => {
        const normalizedOrgId = orgId.extractId()
        // Check if already loaded
        this.appSettingLoading = true
        const existingSettings = this.ref_app_settings_map[normalizedOrgId]
        if (existingSettings) {
          observer.next(existingSettings)
          observer.complete()
          this.appSettingLoading = false
          return
        }

        if (!this.ref_user?.id) {
          observer.error(new Error('🔴 setAppSettings: ref_user.id is not defined'))
          this.appSettingLoading = false
          return
        }

        const getWappSettingsRequest: GetWappSettingsRequest = {
          ref_id_org: orgId.extractId(),
          ref_id_user: this.ref_user.id.extractId(),
        }

        cmwGetOrgWappSettingsRequest(getWappSettingsRequest).subscribe({
          next: (response: GenericPayloadResponse) => {
            if (response.res_code === StcsResCode.SUCCESS && response.stcs_wapp_settings) {
              // Parse the API response to frontend format because the sub_section_map structure is in stringified JSON
              const parsedSettings = parseWappSettings(response.stcs_wapp_settings)
              consola.trace(`[STORE SET] Storing App Settings for orgId: ${normalizedOrgId}`, parsedSettings)
              this.ref_app_settings_map[normalizedOrgId] = parsedSettings

              observer.next(parsedSettings)
            }
          },
          error: (error) => {
            consola.error('🔴 setAppSettings error: ', error)
            observer.error(error)
            this.appSettingLoading = false
          },
          complete: () => {
            observer.complete()
            this.appSettingLoading = false
          },
        })
      })
    },

    /**
     * Update organization app settings
     */
    updateOrgAppSettings(orgId: string, settingsConfig: ParsedWappSettingsConfig): Observable<void> {
      return new Observable((observer) => {
        const updateRequest: UpdateOrgWappSettingsRequest = {
          ref_id_org: orgId.extractId(),
          ref_id_user: this.ref_user.id.extractId(),
          stcs_wapp_settings_config: stringifyWappSettingsConfig(settingsConfig),
        }

        cmwUpdateOrgWappSettingsRequest(updateRequest).subscribe({
          next: (response: GenericPayloadResponse) => {
            if (response.res_code === StcsResCode.SUCCESS) {
              // Update local store
              if (this.ref_app_settings_map[orgId.extractId()]) {
                consola.trace(`[STORE SET] Updating Org App Settings for orgId: ${orgId.extractId()}`, settingsConfig)
                this.ref_app_settings_map[orgId.extractId()].org = settingsConfig
              }
              observer.next()
            } else {
              observer.error(new Error('Failed to update org settings'))
            }
          },
          error: (error) => {
            consola.error('🔴 updateOrgAppSettings error: ', error)
            observer.error(error)
          },
          complete: () => observer.complete(),
        })
      })
    },

    /**
     * Update company app settings
     */
    updateCompAppSettings(orgId: string, compId: string, settingsConfig: ParsedWappSettingsConfig): Observable<void> {
      return new Observable((observer) => {
        const updateRequest: UpdateCompWappSettingsRequest = {
          ref_id_org: orgId.extractId(),
          ref_id_comp: compId.extractId(),
          ref_id_user: this.ref_user.id.extractId(),
          stcs_wapp_settings_config: stringifyWappSettingsConfig(settingsConfig),
        }
        cmwUpdateCompWappSettingsRequest(updateRequest).subscribe({
          // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
          next: (response: GenericPayloadResponse) => {
            if (response.res_code === StcsResCode.SUCCESS) {
              // Update local store
              if (this.ref_app_settings_map[orgId.extractId()]) {
                const existingCompSettings = this.ref_app_settings_map[orgId.extractId()].comp[compId.extractId()]
                consola.trace(`[STORE SET] Updating Comp App Settings for compId: ${compId.extractId()}`, settingsConfig)

                // Filter out user-specific settings keys (keys starting with 'user_settings_') to maintain type safety
                const compOnlySubSectionMap: Record<string, string | CompanyFinancialSettings | CompanyAnalysisSettings | CompanyDashboardSettings | undefined> = {}
                for (const [key, value] of Object.entries(settingsConfig.sub_section_map)) {
                  if (!key.startsWith('user_settings_')) {
                    compOnlySubSectionMap[key] = value as string | CompanyFinancialSettings | CompanyAnalysisSettings | CompanyDashboardSettings | undefined
                  }
                }

                this.ref_app_settings_map[orgId.extractId()].comp[compId.extractId()] = {
                  sub_section_map: compOnlySubSectionMap,
                  user: existingCompSettings?.user || {},
                }
              }
              observer.next()
            } else {
              observer.error(new Error('Failed to update company settings'))
            }
          },
          error: (error) => {
            consola.error('🔴 updateCompAppSettings error: ', error)
            observer.error(error)
          },
          complete: () => observer.complete(),
        })
      })
    },

    /**
     * Update user app settings for a specific company
     */
    updateUserAppSettings(orgId: string, compId: string, settingsConfig: ParsedWappUserSettingsConfig): Observable<void> {
      return new Observable((observer) => {
        const userId = this.ref_user.id.extractId()

        const updateRequest: UpdateCompUserWappSettingsRequest = {
          ref_id_user: userId,
          ref_id_org: orgId.extractId(),
          ref_id_comp: compId.extractId(),
          stcs_wapp_settings_config: stringifyWappUserSettingsConfig(settingsConfig),
        }

        cmwUpdateCompUserWappSettingsRequest(updateRequest).subscribe({
          next: (response: GenericPayloadResponse) => {
            if (response.res_code === StcsResCode.SUCCESS) {
              // Update local store
              if (this.ref_app_settings_map[orgId.extractId()]) {
                if (!this.ref_app_settings_map[orgId.extractId()].comp[compId.extractId()]) {
                  this.ref_app_settings_map[orgId.extractId()].comp[compId.extractId()] = {
                    sub_section_map: {},
                    user: {},
                  }
                }
                consola.trace(`[STORE SET] Updating User App Settings for userId: ${userId} in compId: ${compId.extractId()}`, settingsConfig)
                this.ref_app_settings_map[orgId.extractId()].comp[compId.extractId()].user[userId] = settingsConfig
              }
              observer.next()
            } else {
              observer.error(new Error('Failed to update user settings'))
            }
          },
          error: (error: Error) => {
            consola.error('🔴 updateUserAppSettings error: ', error)
            observer.error(error)
          },
          complete: () => observer.complete(),
        })
      })
    },

    /**
     * Get app settings for a specific scope
     */
    getAppSettings(orgId: string, compId?: string): ParsedWappSettingsConfig | undefined {
      const orgSettings = this.ref_app_settings_map[orgId.extractId()]
      if (!orgSettings) return undefined

      if (compId) {
        // Return company-specific settings if available, fallback to org settings
        return orgSettings.comp[compId.extractId()] || orgSettings.org
      }

      return orgSettings.org
    },

    /**
     * Get user app settings for a specific company
     */
    getUserAppSettings(orgId: string, compId: string): ParsedWappUserSettingsConfig | undefined {
      const orgSettings = this.ref_app_settings_map[orgId.extractId()]
      if (!orgSettings) return undefined

      const compSettings = orgSettings.comp[compId.extractId()]
      if (!compSettings) return undefined

      const userId = this.ref_user.id.extractId()
      return compSettings.user?.[userId]
    },

    /**
     * Get current user's org-specific settings (per user, per org).
     * Returns user-specific settings stored at the org level.
     * @returns Current user's org-specific settings or undefined if not available
     */
    getCurrentUserOrgSettings(): UserOrgSpecificSettings | undefined {
      const hasRequiredIds = this.active_org_id && this.ref_user?.id
      if (!hasRequiredIds) {
        return undefined
      }

      const orgSettings = this.ref_app_settings_map[this.active_org_id.extractId()]
      if (!orgSettings?.org?.sub_section_map) {
        return undefined
      }

      // User settings are stored with key pattern: user_settings_{userId}
      const userSettingsKey = `user_settings_${this.ref_user.id.extractId()}`
      const userSettings = orgSettings.org.sub_section_map[userSettingsKey] as UserOrgSpecificSettings | undefined

      return userSettings
    },

    /**
     * Update user's org-specific settings (per user, per org).
     * Stores settings at the org level, not per company.
     * @param orgId - Organization ID
     * @param userOrgSettings - User's org-specific settings
     */
    updateUserOrgSettings(orgId: string, userOrgSettings: UserOrgSpecificSettings): Observable<void> {
      return new Observable((observer) => {
        if (!this.ref_user?.id) {
          observer.error(new Error('User ID not available'))
          return
        }

        const userId = this.ref_user.id.extractId()
        const orgSettings = this.ref_app_settings_map[orgId.extractId()]

        if (!orgSettings) {
          observer.error(new Error('Org settings not loaded'))
          return
        }

        // User settings stored with key pattern: user_settings_{userId}
        const userSettingsKey = `user_settings_${userId}`

        const updatedOrgSettings: ParsedWappSettingsConfig = {
          sub_section_map: {
            ...orgSettings.org.sub_section_map,
            [userSettingsKey]: userOrgSettings,
          },
        }

        this.updateOrgAppSettings(orgId, updatedOrgSettings).subscribe({
          next: () => observer.next(),
          error: (error) => observer.error(error),
          complete: () => observer.complete(),
        })
      })
    },

    /**
     * Fetch available plans for the active company and store them in state
     * @param compId - Optional company ID, defaults to active_comp_id
     * @param orgId - Optional organization ID, defaults to active_org_id
     * @returns Observable that emits the plan list
     */
    /**
     * Fetch available plans for the active company and store them in state.
     * @param compId - Optional company ID, defaults to active_comp_id
     * @param orgId - Optional organization ID, defaults to active_org_id
     * @param options.backgroundRefresh - When true, bypasses the cache check and silently
     *   re-fetches from the server (SWR pattern). Existing data stays visible.
     * @returns Observable that emits the plan list
     */
    fetchAvailablePlans(compId?: string, orgId?: string, options?: { backgroundRefresh?: boolean }): Observable<FinancialsGridDataAvailablePlanList[]> {
      return new Observable((observer) => {
        const targetCompId = compId || this.active_comp_id
        const targetOrgId = orgId || this.active_org_id
        const isBackground = options?.backgroundRefresh

        if (!targetOrgId) {
          observer.error(new Error('🔴 fetchAvailablePlans: No organization ID provided and no active organization'))
          return
        }

        // Check if plans already exist for this company (skip when background-refreshing)
        if (!isBackground) {
          const existingPlans = this.ref_comp_map[targetCompId]?.availablePlanList
          if (existingPlans && existingPlans.length > 0) {
            consola.debug('🟢 Plans already available for company:', targetCompId)
            observer.next(existingPlans)
            observer.complete()
            return
          }
        }

        if (!this.ref_user?.id) {
          observer.error(new Error('🔴 fetchAvailablePlans: ref_user.id is not defined'))
          return
        }

        const request: CompPlanCfgListRequest = {
          ref_id_comp: targetCompId,
          ref_id_org: targetOrgId,
          ref_id_user: this.ref_user.id.extractId(),
        }

        consola.debug('🔵 Fetching plans from server for company:', targetCompId)

        cmwCompPlanCfgListRequest(request).subscribe({
          next: (response: GenericPayloadResponse) => {
            this.handlePlanFetchResponse(response, targetCompId, observer)

            // After successfully fetching plans, try to load the selected plan result values from app settings.
            // Skip on the planning section — planningview.store handles its own data loading
            // via getPlanConfig (comp_plan_cfg_get) with LRU caching, avoiding duplicate API calls.
            if (response.res_code === StcsResCode.SUCCESS && response.stcs_comp_plan_cfg_list?.list && this.active_section !== 'plan') {
              this.loadSelectedPlanFromAppSettings(targetCompId, targetOrgId).subscribe({
                next: () => {
                  consola.debug('🟢 Selected plan result values loaded from app settings')
                },
                error: (error) => {
                  consola.warn('🟡 Could not load selected plan result values from app settings:', error)
                },
              })
            }
          },
          error: (error) => {
            consola.error('🔴 fetchAvailablePlans error:', error)
            observer.error(error)
          },
          complete: () => observer.complete(),
        })
      })
    },

    /**
     * Handle the response from plan fetch API
     * @param response - API response
     * @param targetCompId - Company ID
     * @param observer - Observable observer
     */
    handlePlanFetchResponse(response: GenericPayloadResponse, targetCompId: string, observer: Observer<FinancialsGridDataAvailablePlanList[]>) {
      if (response.res_code === StcsResCode.SUCCESS && response.stcs_comp_plan_cfg_list?.list) {
        // Map the response to the expected format
        const mappedPlans: FinancialsGridDataAvailablePlanList[] = response.stcs_comp_plan_cfg_list.list
          .map((plan) => ({
            ...plan,
            id: plan.ref_id_comp_plan_cfg.extractId(),
            is_selected: false,
          }))
          .sort((a, b) => new Date(b.obj_updated_iso_utc).getTime() - new Date(a.obj_updated_iso_utc).getTime())

        // Store the plans in the company's data
        if (this.ref_comp_map[targetCompId]) {
          consola.trace(`[STORE SET] Storing Available Plan List for compId: ${targetCompId}`, mappedPlans)
          this.ref_comp_map[targetCompId].availablePlanList = mappedPlans
          consola.debug('🟢 Successfully stored plans for company:', targetCompId, `(${mappedPlans.length} plans)`)
        }

        observer.next(mappedPlans)
      } else if (response.res_code === StcsResCode.NOT_FOUND || !response.stcs_comp_plan_cfg_list?.list) {
        // No plans found, set empty array
        if (this.ref_comp_map[targetCompId]) {
          consola.trace(`[STORE SET] Clearing Available Plan List for compId: ${targetCompId}`)
          this.ref_comp_map[targetCompId].availablePlanList = []
        }
        consola.debug('🟡 No plans found for company:', targetCompId)
        observer.next([])
      } else {
        consola.error('🔴 Failed to fetch plans:', response.res_code, response.res_message)
        observer.error(new Error(`Failed to fetch plans: ${response.res_message || 'Unknown error'}`))
      }
    },

    /**
     * Load plan result values for a specific plan and store them in state
     * @param compId - Company ID
     * @param planCfgId - Plan configuration ID
     * @param orgId - Organization ID
     * @returns Observable that emits when the plan data is loaded
     */
    /**
     * Load plan result values for a specific plan and store them in state.
     * @param compId - Company ID
     * @param planCfgId - Plan configuration ID
     * @param orgId - Organization ID
     * @param options.backgroundRefresh - When true, bypasses the cache check and silently
     *   re-fetches from the server (SWR pattern). Existing data stays visible.
     * @returns Observable that emits when the plan data is loaded
     */
    loadPlanResValues(compId: string, planCfgId: string, orgId: string, options?: { backgroundRefresh?: boolean }): Observable<StcsCompPlanResValues | undefined> {
      return new Observable((observer) => {
        const isBackground = options?.backgroundRefresh

        if (!planCfgId) {
          observer.error(new Error('🔴 loadPlanResValues: Plan configuration ID is required'))
          return
        }
        if (!compId) {
          observer.error(new Error('🔴 loadPlanResValues: Company ID is required'))
          return
        }
        if (!orgId) {
          observer.error(new Error('🔴 loadPlanResValues: Organization ID is required'))
          return
        }

        // Initialize ref_comp_plans if it doesn't exist
        if (!this.ref_comp_map[compId].ref_comp_plans) {
          this.ref_comp_map[compId].ref_comp_plans = {}
        }

        // Check if data already exists for this plan (skip when background-refreshing)
        if (!isBackground) {
          const existingPlanData = this.ref_comp_map[compId].ref_comp_plans[planCfgId]
          if (existingPlanData?.ref_comp_plan_res_values) {
            consola.debug('🟢 Plan result values already available for plan:', planCfgId)
            observer.next(existingPlanData.ref_comp_plan_res_values)
            observer.complete()
            return
          }
        }

        if (!this.ref_user?.id) {
          observer.error(new Error('🔴 loadPlanResValues: ref_user.id is not defined'))
          return
        }

        const request: CompPlanCfgGetRequest = {
          ref_id_user: this.ref_user.id.extractId(),
          ref_id_org: orgId,
          ref_id_comp_plan_cfg: planCfgId,
        }

        consola.debug('🔵 Fetching plan result values from server for plan:', planCfgId)

        cmwCompPlanCfgGetRequest(request).subscribe({
          next: (message) => {
            // Initialize plan data if it doesn't exist
            if (!this.ref_comp_map[compId].ref_comp_plans?.[planCfgId]) {
              this.ref_comp_map[compId].ref_comp_plans = this.ref_comp_map[compId].ref_comp_plans || {}
              this.ref_comp_map[compId].ref_comp_plans[planCfgId] = {}
            }

            if (message.res_code === StcsResCode.SUCCESS && message.res_payload && message.res_payload.stcs_comp_plan_res_values) {
              const dataToSave = message.res_payload.stcs_comp_plan_res_values
              consola.trace(`[STORE SET] Storing Plan Result Values for planId: ${planCfgId}`, dataToSave)
              this.ref_comp_map[compId].ref_comp_plans[planCfgId].ref_comp_plan_res_values = dataToSave
              consola.debug('🟢 Successfully loaded plan result values for plan:', planCfgId)
              observer.next(message.res_payload.stcs_comp_plan_res_values)
            } else if (message.res_code === StcsResCode.NOT_FOUND) {
              consola.warn('🟡 No plan result values found for plan:', planCfgId)
              const emptyValues = {} as StcsCompPlanResValues
              consola.trace(`[STORE SET] Storing EMPTY Plan Result Values for planId: ${planCfgId}`, emptyValues)
              this.ref_comp_map[compId].ref_comp_plans[planCfgId].ref_comp_plan_res_values = emptyValues
              observer.next(emptyValues)
            } else {
              consola.error('🔴 Error loading plan result values:', message.res_code, message.res_message)
              const emptyValues = {} as StcsCompPlanResValues
              consola.trace(`[STORE SET] Storing EMPTY Plan Result Values on ERROR for planId: ${planCfgId}`, emptyValues)
              this.ref_comp_map[compId].ref_comp_plans[planCfgId].ref_comp_plan_res_values = emptyValues
              observer.next(emptyValues)
            }
          },
          error: (error) => {
            consola.error('🔴 loadPlanResValues error:', error)
            observer.error(error)
          },
          complete: () => observer.complete(),
        })
      })
    },

    /**
     * Load KPI result values for a specific KPI and store them in state
     * @param orgId - Organization ID
     * @param compId - Company ID
     * @param planId - KPI configuration ID
     * @param lastActual - Last actual date string
     * @param isCfg - If true, fetches KPI definitions (StcsCompKpiCfg). If false, fetches calculated results (StcsCompKpiResValues).
     * @param forceUnsettled - If true, resets settled state to false and shows loading until fresh response returns.
     * @returns Observable that emits when the KPI data is loaded
     */
    loadKpiResValues(orgId: string, compId: string, planId?: string, lastActual?: string, isCfg = false, forceUnsettled = false): Observable<StcsCompKpiCfg | StcsCompKpiResValues | undefined> {
      return new Observable((observer) => {
        if ([orgId, compId].some((id) => !id)) {
          observer.error(new Error('🔴 loadKpiResValues: Missing required IDs (compId or orgId)'))
          return
        }

        const compData = this.ref_comp_map[compId]
        this.initializeKpiStore(compData, isCfg, forceUnsettled)

        if (!this.ref_user?.id) {
          observer.error(new Error('🔴 loadKpiResValues: ref_user.id is not defined'))
          return
        }

        const request: CompKpiResValuesGetRequest = {
          ref_id_user: this.ref_user.id.extractId(),
          ref_id_org: orgId,
          ref_id_comp: compId,
        }

        if (lastActual) {
          request.actuals_end_date_string = lastActual
        }

        if (planId) {
          // Defensive: only send a plan scope if it is actually one of the known plans for this comp.
          // Fresh orgs (or timing windows before plan list / selection settles) can have "0" or other
          // invalid ids in selectedPlan; sending them produces "Invalid response code" + retries on the res endpoint.
          const avail = this.ref_comp_map[compId]?.availablePlanList || []
          if (avail.some((p) => p.id === planId)) {
            request.ref_id_comp_plan_cfg = planId
          } else {
            consola.debug(`[KPI] Omitting invalid/unknown planId ${planId} from KPI request for comp ${compId}`)
          }
        }

        const errorHandler = (error: Error) => {
          this.setKpiRequestStatus(compData, isCfg ? 'cfg' : 'res', false, true, error?.message || 'KPI response failed')
          consola.error(`🔴 loadKpiResValues (${isCfg ? 'Cfg' : 'Res'}) error:`, error)
          observer.error(error)
        }

        const completeHandler = () => observer.complete()

        if (isCfg) {
          cmwCompKpiCfgCalcRequest(request)
            .pipe(first(), retry({ delay: (_error) => timer(1000), count: 5 }))
            .subscribe({
              next: (message) => {
                const kpiConfig = message.res_payload?.stcs_comp_kpi_cfg
                if (message.res_code === StcsResCode.SUCCESS && kpiConfig) {
                  this.storeKpiData(compData, kpiConfig, true)
                  observer.next(kpiConfig)
                } else {
                  consola.error('🔴 No KPI Config found:', message.res_code, message.res_message)
                  this.storeKpiData(compData, {} as StcsCompKpiCfg, true)
                  observer.next(undefined)
                }
              },
              error: errorHandler,
              complete: completeHandler,
            })
        } else {
          cmwCompKpiResValuesGetRequest(request)
            .pipe(first(), retry({ delay: (_error) => timer(1000), count: 5 }))
            .subscribe({
              next: (message) => {
                const kpiValues = message.stcs_comp_kpi_res_values
                if (message.res_code === StcsResCode.SUCCESS && kpiValues) {
                  this.storeKpiData(compData, kpiValues, false)
                  observer.next(kpiValues)
                } else {
                  consola.error('🔴 No KPI Result Values found:', message.res_code, message.res_message)
                  this.storeKpiData(compData, {} as StcsCompKpiResValues, false)
                  observer.next(undefined)
                }
              },
              error: errorHandler,
              complete: completeHandler,
            })
        }
      })
    },

    /**
     * Clear cached RF plan data and in-flight RF requests.
     * Use this after plan edits so financial views fetch fresh RF values.
     */
    clearRFPlanCache(compId: string, planCfgId: string): void {
      if (!compId) {
        return
      }

      const compData = this.ref_comp_map[compId]
      if (!compData) {
        return
      }

      const clearPendingRequestEntries = (targetPlanId: string) => {
        const pendingKeys = Array.from(pendingRFPlanRequests.keys())
        for (const key of pendingKeys) {
          if (key.startsWith(`${targetPlanId}_`)) {
            pendingRFPlanRequests.delete(key)
          }
        }
      }

      if (compData.ref_comp_rf_plans?.[planCfgId]) {
        delete compData.ref_comp_rf_plans[planCfgId]
      }
      clearPendingRequestEntries(planCfgId)
    },

    loadRFPlanResValues(compId: string, planCfgId: string, orgId: string, lastActual: string, options?: { backgroundRefresh?: boolean }): Observable<StcsCompPlanResValues | undefined> {
      consola.box(`loadRFPlanResValues CALLED FOR compId: ${compId} | planCfgId: ${planCfgId}`)
      consola.debug('[RF LOG] Function arguments:', { compId, planCfgId, orgId, lastActual })

      const nextMonth = moment(lastActual, 'YYYY-MM').add(1, 'month').format('YYYY-MM')
      const cacheKey = `${planCfgId}_${nextMonth}`
      consola.debug(`[RF LOG] Generated cacheKey: ${cacheKey}`)

      consola.debug('[RF LOG] --- CACHE CHECK ---')
      consola.debug(`[RF LOG] Checking for existing data in store path: this.ref_comp_map[${compId}]?.ref_comp_rf_plans?.[${planCfgId}]?.ref_comp_plan_rf_res_values`)
      const existingRFData = this.ref_comp_map[compId]?.ref_comp_rf_plans?.[planCfgId]?.ref_comp_plan_rf_res_values
      consola.debug('[RF LOG] Found existing data in store:', JSON.parse(JSON.stringify(existingRFData || {})))
      consola.debug('[RF LOG] Checking condition: (existingRFData && existingRFData.meta?.fpna_begin_date_string === nextMonth)')
      consola.debug('[RF LOG]   - existingRFData is truthy?', !!existingRFData)
      if (existingRFData) {
        consola.debug('[RF LOG]   - existingRFData.meta?.fpna_begin_date_string:', existingRFData.meta?.fpna_begin_date_string)
        consola.debug('[RF LOG]   - nextMonth:', nextMonth)
        consola.debug('[RF LOG]   - Dates match?', existingRFData.meta?.fpna_begin_date_string === nextMonth)
      }

      // SWR: For non-background callers, return cached data immediately even if a background
      // refresh is in-flight. This prevents grids from showing loading when revalidateSection
      // silently refreshes data in the background.
      if (!options?.backgroundRefresh && existingRFData?.id && existingRFData.meta?.fpna_begin_date_string === nextMonth) {
        consola.success(`[RF LOG] ✅ CACHE HIT. Returning cached data for key: ${cacheKey}`)
        return new Observable((observer) => {
          observer.next(existingRFData)
          observer.complete()
        })
      }

      // Only check for in-flight requests AFTER the cache check.
      // Background callers skip the cache above and share the in-flight request if one exists.
      const pendingRequestCacheKey = pendingRFPlanRequests.get(cacheKey)

      if (pendingRequestCacheKey) {
        consola.warn(`[RF LOG] 🔄 Returning PENDING (in-flight) request from cache for key: ${cacheKey}`)
        return pendingRequestCacheKey
      }

      consola.warn(`[RF LOG] ❌ CACHE MISS. Proceeding to fetch data for key: ${cacheKey}`)

      const newRequestObservable = new Observable((observer: Observer<StcsCompPlanResValues | undefined>) => {
        if (!(planCfgId && compId && orgId)) {
          observer.error(new Error('🔴 loadRFPlanResValues: Missing required IDs'))
          return
        }

        // Check if start date from plan data is after nextMonth
        let endDateString = nextMonth
        const getPlanData = this.ref_comp_map[compId].ref_comp_plans?.[planCfgId]?.ref_comp_plan_res_values
        const planEndDate = moment(getPlanData?.meta?.fpna_begin_date_string)
        const endMomentDate = moment(nextMonth)

        if (planEndDate.isAfter(endMomentDate)) {
          endDateString = getPlanData?.meta?.fpna_begin_date_string || nextMonth
        }

        if (!this.ref_comp_map[compId].ref_comp_rf_plans) {
          this.ref_comp_map[compId].ref_comp_rf_plans = {}
        }

        if (!this.ref_user?.id) {
          observer.error(new Error('🔴 loadPlanResValues: ref_user.id is not defined'))
          return
        }

        const rFResRequest: CompPlanRfGetRequest = {
          ref_id_user: this.ref_user.id.extractId(),
          ref_id_org: orgId,
          ref_id_comp: compId,
          ref_id_comp_plan_cfg: planCfgId,
          fpna_begin_date_string: endDateString,
        }

        consola.debug(`[RF LOG] 🔵 Preparing to fetch RF Plan result values from server for key: ${cacheKey}`)
        cmwCompPlanRfGetRequest(rFResRequest)
          .pipe(first(), retry({ delay: (_error) => timer(1000), count: 3 }))
          .subscribe({
            next: (planRfResponse) => {
              consola.debug('[RF LOG] --- SAVE PROCESS ---')
              consola.debug('[RF LOG] Received API response:', { res_code: planRfResponse.res_code, res_message: planRfResponse.res_message })
              const compDataBeforeSave = this.ref_comp_map[compId]
              consola.debug(`[RF LOG] State of ref_comp_map[${compId}] BEFORE save:`, JSON.parse(JSON.stringify(compDataBeforeSave)))

              const rfPlans = this.ref_comp_map[compId].ref_comp_rf_plans

              if (!rfPlans) {
                consola.error(`[RF LOG] [CRITICAL] Inconsistent state: ref_comp_rf_plans is missing for compId: ${compId}`)
                return
              }

              if (!rfPlans[planCfgId]) {
                rfPlans[planCfgId] = {}
              }

              const planData = rfPlans[planCfgId]

              if (planRfResponse.res_code === StcsResCode.SUCCESS) {
                const dataToSave = planRfResponse.res_payload?.stcs_comp_plan_rf_res_values
                // IMPORTANT:
                // HACKY!!!!! Overwrite reference month to match response of RF endpoint
                //  This is needed because RF endpoint requests nextMonth, but returns start date for this key
                //
                // Should be fixed in backend later to return dedicated check value.
                //
                if (dataToSave?.meta) {
                  dataToSave.meta.fpna_begin_date_string = nextMonth
                }
                consola.debug('[RF LOG] API call SUCCESS. Data to save:', JSON.parse(JSON.stringify(dataToSave || {})))
                planData.ref_comp_plan_rf_res_values = dataToSave
                observer.next(dataToSave)
              } else {
                consola.error(`[RF LOG] API call FAILED or returned no data. Saving empty object. Response code: ${planRfResponse.res_code}`)
                const emptyValues = {} as StcsCompPlanResValues
                planData.ref_comp_plan_rf_res_values = emptyValues
                observer.next(emptyValues)
              }

              const compDataAfterSave = this.ref_comp_map[compId]
              consola.debug(`[RF LOG] State of ref_comp_map[${compId}] AFTER save:`, JSON.parse(JSON.stringify(compDataAfterSave)))
              consola.success(`[RF LOG] ✅ Save process complete for key: ${cacheKey}`)
            },
            error: (error) => {
              consola.error('[RF LOG] 🔴 RXJS Error in subscribe block:', error)
              observer.error(error)
            },
            complete: () => {
              consola.debug('[RF LOG] 🟢 RXJS stream complete.')
              observer.complete()
            },
          })
      }).pipe(
        shareReplay(1),
        tap({
          finalize: () => {
            pendingRFPlanRequests.delete(cacheKey)
            consola.debug('🚮 Cleaned up pending RF Plan request:', cacheKey)
          },
        }),
      )

      pendingRFPlanRequests.set(cacheKey, newRequestObservable)
      return newRequestObservable
    },

    /**
     * Load the selected plan result values from app settings for the financials section
     * @param compId - Company ID
     * @param orgId - Organization ID
     * @returns Observable that emits when the selected plan data is loaded
     */
    loadSelectedPlanFromAppSettings(compId: string, orgId: string): Observable<void> {
      return new Observable((observer) => {
        // Check if app settings are available
        const appSettings = this.ref_app_settings_map[orgId.extractId()]
        if (!appSettings) {
          consola.debug('🟡 App settings not available yet for loading selected plan')
          observer.next()
          observer.complete()
          return
        }

        if (!this.ref_user?.id) {
          consola.debug('🟡 User ID not available for loading selected plan')
          observer.next()
          observer.complete()
          return
        }

        try {
          // Get the selected plan ID from sharedScopeStore (consistent with other components)
          const sharedScopeStore = useSharedScopeStore()
          const selectedPlanId = sharedScopeStore.getSelectPlanId(compId)

          if (selectedPlanId) {
            consola.debug('🔵 Loading plan result values for selected plan from app settings:', selectedPlanId)

            this.loadPlanResValues(compId, selectedPlanId, orgId).subscribe({
              next: () => {
                consola.debug('🟢 Successfully loaded selected plan result values from app settings')
                observer.next()
              },
              error: (error) => {
                consola.error('🔴 Failed to load selected plan result values from app settings:', error)
                observer.next() // Don't fail the whole process if plan loading fails
              },
              complete: () => observer.complete(),
            })
          } else {
            consola.debug('🟡 No selected plan found in user app settings for financials section')
            observer.next()
            observer.complete()
          }
        } catch (error) {
          consola.error('🔴 Error accessing app settings for selected plan:', error)
          observer.next()
          observer.complete()
        }
      })
    },
    getOrgRoleForUser(orgId: string, userId: string): StcsOrgRoleEnum | undefined {
      consola.info(`Fetching Org Role for User ${userId} in Org ${orgId}`)
      const orgRole = this.ref_org_map[orgId]?.meta?.org_role_map[userId]
      consola.info(`User Org Role for Org ${orgId}:`, orgRole)
      return orgRole
    },
    getCompRoleForUser(orgId: string, compId: string, userId: string): StcsCompRoleEnum | undefined {
      consola.info(`Fetching Comp Role for User ${userId} in Comp ${compId} of Org ${orgId}`)
      const compMap = this.ref_org_map[orgId]?.meta?.ref_comp_map?.[compId]
      if (!compMap) {
        consola.warn(`No company role map found for Comp ${compId} in Org ${orgId}`)
        return undefined
      }
      const compRole = compMap.ref_user_map?.[userId]?.role
      consola.info(`User Comp Role for Comp ${compId}:`, compRole)
      return compRole
    },
  },
  persist: false,
})
